import { delay } from './delay';

interface DownloadFileInfo {
  url: string;
  filename?: string;
}

interface MultiDownloadResult {
  successCount: number;
  failedFiles: string[];
}

/**
 * Triggers a single file download by opening the URL directly.
 * Uses the same approach as individual download buttons (direct link with target="_blank"),
 * which hands off immediately to the browser's native download manager.
 */
const triggerDirectDownload = (url: string): void => {
  const anchor = document.createElement('a');
  anchor.style.display = 'none';
  anchor.href = url;
  anchor.target = '_blank';
  anchor.rel = 'noreferrer';
  document.body.appendChild(anchor);
  anchor.click();

  setTimeout(() => {
    anchor.remove();
  }, 1000);
};

/**
 * Downloads multiple files by triggering direct URL navigation for each one.
 * Unlike the blob-based approach, this immediately starts each download via the
 * browser's native download manager without waiting for the full file to be fetched
 * into memory first.
 */
const multiDownload = async (files: DownloadFileInfo[]): Promise<MultiDownloadResult> => {
  const failedFiles: string[] = [];
  let successCount = 0;

  for (let i = 0; i < files.length; i++) {
    const file = files[i];

    try {
      triggerDirectDownload(file.url);
      successCount++;
    } catch {
      failedFiles.push(file.filename || file.url);
    }

    // Small delay between downloads to avoid browser popup blocking
    if (i < files.length - 1) {
      await delay(300);
    }
  }

  return { successCount, failedFiles };
};

export { multiDownload };
export type { DownloadFileInfo, MultiDownloadResult };
